import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RichText, { detectDir } from "./RichText";
import { toast } from "sonner";
import { MessageSquare, Trash2, Send } from "lucide-react";

export default function PostComments({ postId, isPublic = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Lazy-load comment count once on mount (so the button can show a number)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/feed/${postId}/comments`);
        if (alive) {
          setComments(data);
          setCount(data.length);
          setLoaded(true);
        }
      } catch (e) { /* ignore — may be private */ }
    })();
    return () => { alive = false; };
  }, [postId]);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/feed/${postId}/comments`);
      setComments(data);
      setCount(data.length);
    } catch (e) { /* noop */ }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/feed/${postId}/comments`, { text: text.trim() });
      setText("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const onDelete = async (cid) => {
    try {
      await api.delete(`/feed/comments/${cid}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canComment = !!user && user !== false;

  return (
    <div className="mt-4 border-t border-[#E4E4E7] dark:border-[#2A2A30] pt-3" data-testid={`comments-${postId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`comments-toggle-${postId}`}
        className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] inline-flex items-center gap-2"
      >
        <MessageSquare className="w-3 h-3" />
        {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Add comment"}
        <span className="text-[#D4D4D8]">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`comments-list-${postId}`}>
          {loaded && comments.length === 0 && (
            <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">[ NO COMMENTS YET ]</div>
          )}
          {comments.map((c) => {
            const mine = c.user_id === user?.id;
            const canDelete = mine || user?.role === "admin";
            return (
              <div key={c.id} className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] p-3" data-testid={`comment-${c.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-mono text-[10px] text-[#7C3AED] uppercase tracking-[0.2em]">{c.username}</div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[9px] text-[#71717A] tracking-[0.2em]">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                    {canDelete && (
                      <button
                        onClick={() => onDelete(c.id)}
                        data-testid={`comment-delete-${c.id}`}
                        title="Delete"
                        className="text-[#A1A1AA] hover:text-[#E11D48] transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm text-[#09090B] dark:text-[#F4F4F5] font-body whitespace-pre-wrap break-words"><RichText text={c.text} /></div>
              </div>
            );
          })}

          {canComment ? (
            <form onSubmit={onSubmit} className="flex gap-2" data-testid={`comments-form-${postId}`}>
              <input
                data-testid={`comments-input-${postId}`}
                value={text} dir={detectDir(text) === "rtl" ? "rtl" : "auto"} onChange={(e) => setText(e.target.value)}
                className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED]"
                placeholder="Add a comment…"
                maxLength={2000}
              />
              <button data-testid={`comments-send-${postId}`} type="submit" disabled={sending || !text.trim()} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-3 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50 inline-flex items-center gap-1 text-xs">
                <Send className="w-3 h-3" />
              </button>
            </form>
          ) : (
            <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">[ {isPublic ? "SIGN IN TO COMMENT" : "JOIN THE LEAGUE TO COMMENT"} ]</div>
          )}
        </div>
      )}
    </div>
  );
}
